import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MyMatch } from '@swiss-magic/shared-types';
import { Button } from '@/components/ui/button';
import { respondToMatch } from '@/lib/api/player';
import { ApiError } from '@/lib/api/core';
import { showErrorToast, showSuccessToast } from '@/lib/toast';
import { viewerResult } from './viewerResult';

// Story 010b — inline Confirm/Dispute row, shown on the match the
// opponent just self-reported. Mirrors SelfReportRow visually so the
// "your turn to act on this match" affordances live in one place. The
// backend respond endpoint is idempotent — a stale tap (director or
// opponent already moved on) returns alreadyHandled and we just
// refetch.
export function RespondRow({
  tournamentId,
  match,
  onResponded,
}: {
  tournamentId: string;
  match: MyMatch;
  onResponded: () => void;
}) {
  const { t } = useTranslation('player');
  const [submitting, setSubmitting] = useState<'confirm' | 'dispute' | null>(
    null,
  );
  // The result is stored from the first player's perspective; show the
  // viewer their own score first to match the rest of the UI.
  const displayScore = viewerResult(match);
  const submit = async (action: 'confirm' | 'dispute') => {
    if (submitting) return;
    setSubmitting(action);
    try {
      const res = await respondToMatch(
        tournamentId,
        match.roundNumber,
        match.tableNumber,
        action,
      );
      if (res.alreadyHandled) {
        showSuccessToast(t('my_matches.respond.already_resolved'));
      } else if (action === 'confirm') {
        showSuccessToast(
          res.finalized
            ? t('my_matches.respond.confirmed_final')
            : t('my_matches.respond.confirmed_acknowledged'),
        );
      } else {
        showSuccessToast(t('my_matches.respond.disputed'));
      }
      onResponded();
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.userMessage : t('my_matches.respond.error');
      showErrorToast(msg);
    } finally {
      setSubmitting(null);
    }
  };
  return (
    <div
      data-testid={`my-match-respond-${match.roundNumber}`}
      className="mt-1 flex flex-col gap-2 border-t pt-3"
    >
      <p className="text-xs text-muted-foreground">
        {displayScore
          ? t('my_matches.respond.prompt_with_score', { score: displayScore })
          : t('my_matches.respond.prompt_no_score')}
      </p>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          className="flex-1"
          data-testid={`my-match-respond-confirm-${match.roundNumber}`}
          disabled={submitting !== null}
          onClick={() => submit('confirm')}
        >
          {submitting === 'confirm'
            ? t('my_matches.respond.confirming')
            : t('my_matches.respond.confirm')}
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="flex-1 text-red-600 border-red-300 hover:bg-red-50"
          data-testid={`my-match-respond-dispute-${match.roundNumber}`}
          disabled={submitting !== null}
          onClick={() => submit('dispute')}
        >
          {submitting === 'dispute'
            ? t('my_matches.respond.disputing')
            : t('my_matches.respond.dispute')}
        </Button>
      </div>
    </div>
  );
}
